import {
  CheckIcon,
  Chip,
  ChipColorEnum,
  ChipShapeEnum,
  ChipSizeEnum,
  ClearIcon,
  CloseIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  FormInputSizeEnum,
  IconButton,
  IconButtonSizeEnum,
  IconSizeEnum,
  InlineList,
  InputColorEnum,
  InputSearch,
  SearchIcon,
  TChipProps,
  Typography,
  matchString,
} from '@acceso/design-system';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { compareListBoxDataItemsByLabel } from '@/helpers/sorters';
import { _get } from '@/utils/_get';

import { useListbox } from './ListboxContext';
import ListboxItem from './ListboxItem';
import { ListboxDataItem, ListboxRootProps } from './ListboxTypes';

const defaultIdentifiers = {
  key: 'key',
  label: 'label',
  value: 'value',
  icon: 'icon',
  children: 'children',
  image: 'image',
};

function ListboxRoot<T>({
  classes = {},
  onSelectItem,
  items: itemsProp,
  maxSelectedListItems = 5,
  showSearchbox = true,
  identifiers: identifiersProp,
  dark,
  groups,
  renderChip,
  noApplies = false,
  noClean = false,
  noSave = false,
  noSearch = false,
  isFirstOpen = false,
  onSearch,
  noSort = false,
  listFooter,
  serverSearch = false,
}: ListboxRootProps<T>) {
  const { t } = useTranslation();
  const { selected, setSelected, mode, onClose } = useListbox();
  const identifiers = useMemo(
    () => ({
      ...defaultIdentifiers,
      ...identifiersProp,
    }),
    [identifiersProp]
  );

  const items: ListboxDataItem[] = useMemo(() => {
    const parse = (item: any): ListboxDataItem => ({
      ...item,
      key: _get(item, identifiers.key),
      label: _get(item, identifiers.label),
      value: _get(item, identifiers.value),
      icon: _get(item, identifiers.icon),
      children: (item[identifiers.children] ?? []).map((subitem: any) => parse(subitem)),
      image: _get(item, identifiers.image),
    });

    return itemsProp.reduce((acc: ListboxDataItem[], item: any) => {
      acc.push(parse(item));
      return acc;
    }, [] as ListboxDataItem[]);
  }, [identifiers, itemsProp]);

  const [search, setSearch] = useState('');

  const flattenItems = useCallback((items: ListboxDataItem[]): ListboxDataItem[] => {
    const result: ListboxDataItem[] = [];

    const flatten = (itemArray: ListboxDataItem[]) => {
      itemArray.forEach(item => {
        result.push(item);
        if (item.children && item.children.length > 0) {
          flatten(item.children);
        }
      });
    };

    flatten(items);
    return result;
  }, []);

  const allItems = useMemo(() => flattenItems(items), [items, flattenItems]);

  const onClear = useCallback(() => {
    setSelected([]);
  }, [setSelected]);

  const onChangeSearch = useCallback((value: string) => {
    setSearch(value);
    onSearch && onSearch(value);
  }, []);

  const itemsFiltered: ListboxDataItem[] = useMemo(() => {
    // The consumer resolved the term against the server, so the items already are the
    // result. Filtering them again by label would drop a logo the backend matched on its
    // description, and re-sorting would contradict the order the backend returned.
    if (serverSearch) return items;

    const filterArray = (array: ListboxDataItem[]) => {
      return array.reduce((acc, item) => {
        if (matchString(item.label, search)) {
          acc.push(item);
          return acc;
        }

        if (item.children) {
          const filteredChildren = filterArray(item.children);
          if (filteredChildren.length > 0) {
            // Return the parent and the children that match the search term
            const newItem = { ...item, children: filteredChildren };
            acc.push(newItem);
          }
          return acc;
        }

        return acc;
      }, [] as ListboxDataItem[]);
    };

    if (noSort) {
      return filterArray(items);
    }

    return filterArray(items).sort(compareListBoxDataItemsByLabel);
  }, [items, search, serverSearch, noSort]);

  const itemWithChildren = itemsFiltered.find(item => item.children?.length) ? true : false;

  const selectedItems = useMemo(
    () => allItems.filter(item => selected.includes(item.value)),
    [selected, allItems]
  );

  const isGrouped = groups && groups.length > 0;

  return (
    <div className={clsx('flex-1', dark ? 'dark' : null, classes.root)}>
      <div className={clsx('mt-1 rounded')}>
        <div
          className={clsx('flex h-full flex-col', {
            'p-2': dark,
          })}
        >
          {showSearchbox && !noSearch && (
            <div className="mb-3.5">
              <InputSearch
                size={FormInputSizeEnum.ExtraSmall}
                color={dark ? InputColorEnum.DARK : InputColorEnum.NEUTRAL}
                placeholder={t('Buscar...')}
                onChange={onChangeSearch}
                options={{
                  rightContent: {
                    icon: SearchIcon,
                  },
                }}
              />
            </div>
          )}
          <div className="flex max-h-60 flex-col overflow-y-auto">
            {isGrouped
              ? groups.map(({ label, filter }) => {
                  return (
                    <>
                      <div
                        key={label}
                        id={label}
                        className="w-full border-b-[1px] border-stroke-20 px-size-12 py-size-8"
                      >
                        <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Medium}>
                          {label}
                        </Typography>
                      </div>

                      {filter(itemsFiltered).map((item, index) => {
                        return (
                          <ListboxItem
                            dark={dark || false}
                            key={`${label}_${index}`}
                            item={item}
                            onSelect={onSelectItem}
                            color={dark ? FontColorEnum.Medium : FontColorEnum.Default}
                            className={clsx({
                              'ml-6': !item.children?.length && itemWithChildren,
                            })}
                          />
                        );
                      })}
                    </>
                  );
                })
              : (() => {
                  const selectAll = itemsFiltered.find(item => item.isSelectAll);
                  const rest = itemsFiltered.filter(item => !item.isSelectAll);
                  const renderList = selectAll ? [selectAll, ...rest] : rest;
                  return renderList.map((item, index) => {
                    return (
                      <ListboxItem
                        isFirstOpen={isFirstOpen && index === 0}
                        isRoot={index === 0}
                        dark={dark || false}
                        key={index}
                        item={item}
                        onSelect={onSelectItem}
                        color={dark ? FontColorEnum.Medium : FontColorEnum.Default}
                        className={clsx({
                          'ml-6': !item.children?.length && itemWithChildren,
                        })}
                      />
                    );
                  });
                })()}
            {listFooter}
          </div>
        </div>
      </div>
      <Transition
        show={mode === 'multiple' && selected.length > 0}
        as="div"
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-75"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        {selected.length > 0 && !noApplies && (
          <div className="border-t border-neutral-30 dark:border-text-medium">
            <div
              className={clsx(
                'dark:bg-transparent',
                'h-8 w-full',
                'px-2 py-2.5',
                'text-tiny',
                'flex items-center justify-between font-semibold'
              )}
            >
              <span
                className={clsx('select-none text-xs font-normal leading-[18px] text-text-medium')}
              >
                <Typography
                  fontSize={FontSizeEnum.Xs}
                  color={dark ? FontColorEnum.Neutral : FontColorEnum.Dark10}
                >
                  {selected.length}
                </Typography>{' '}
                {selected.length > 1 ? (
                  <Typography
                    fontSize={FontSizeEnum.Xs}
                    color={dark ? FontColorEnum.Neutral : FontColorEnum.Dark10}
                  >
                    {t('filtros añadidos')}
                  </Typography>
                ) : (
                  <Typography
                    fontSize={FontSizeEnum.Xs}
                    color={dark ? FontColorEnum.Neutral : FontColorEnum.Dark10}
                  >
                    {t('filtro añadido')}
                  </Typography>
                )}
              </span>
            </div>
            <div className="mb-2 ml-2 mr-2">
              <InlineList
                itemComponent={(item: ListboxDataItem) => {
                  const chipProps: TChipProps = {
                    color: ChipColorEnum.NEUTRAL_0,
                    shape: ChipShapeEnum.ROUNDED,
                    size: ChipSizeEnum.XS,
                    rightIcon: (
                      <CloseIcon
                        size={IconSizeEnum.SM}
                        className="cursor-pointer text-text-dark-10"
                      />
                    ),
                    onClick: () => onSelectItem(item),
                    children: (
                      <Typography fontSize={FontSizeEnum.Xs} weight={FontWeightEnum.Medium}>
                        {item.label}
                      </Typography>
                    ),
                  };

                  return renderChip ? (
                    renderChip(chipProps, item)
                  ) : (
                    <Chip key={item.key} {...chipProps} />
                  );
                }}
                items={selectedItems}
                maxCollapsedItems={maxSelectedListItems}
              />
            </div>

            {noClean && noSave && (
              <div className="flex items-center justify-end gap-[8px]">
                {noClean && (
                  <div className="flex space-x-1">
                    <ClearIcon
                      size={IconSizeEnum.XS}
                      className={clsx('text-text-dark-10', {
                        'text-text-light': dark,
                      })}
                    />

                    <Typography
                      fontSize={FontSizeEnum.Xs}
                      weight={FontWeightEnum.Medium}
                      color={dark ? FontColorEnum.Neutral : FontColorEnum.Dark10}
                      className="cursor-pointer select-none underline"
                      role="button"
                      onClick={onClear}
                    >
                      {t('Limpiar')}
                    </Typography>
                  </div>
                )}

                {noSave && (
                  <IconButton size={IconButtonSizeEnum.SM} onClick={onClose}>
                    {' '}
                    <CheckIcon size={IconSizeEnum.XS} />
                  </IconButton>
                )}
              </div>
            )}
          </div>
        )}
      </Transition>
    </div>
  );
}

export default ListboxRoot;
